export function launchWhisperStorageKey(conf, agentId, launchQuestion) {
  const lq = String(launchQuestion)
  return conf?.chatId
    ? `launch_whisper_${agentId}_${lq}_${conf.chatId}`
    : `launch_whisper_${agentId}_${lq}`
}

/**
 * Build the launch whisper payload (same rules as LaunchConversationEffect).
 *
 * @param {Record<string, unknown>} conf
 * @returns {string|null}
 */
export function buildLaunchWhisperPayload(conf) {
  const agentId = conf?.agentId || conf?.chatId
  if (!agentId) return null

  const launchQuestion = conf?.launchConversationId
  if (launchQuestion == null || launchQuestion === '') return null

  let payload = conf?.widgetOpenedWhisperData
  if (payload == null || String(payload).trim() === '') {
    const lid = launchQuestion
    const suffix =
      lid != null &&
      lid !== '' &&
      lid !== -1 &&
      lid !== '-1' &&
      String(lid).trim() !== ''
        ? String(lid)
        : ''
    payload = `${agentId}ochatbot-launch${suffix}`
  }

  return String(payload)
}

export function isLaunchWhisperMarkedSent(conf, agentId, launchQuestion) {
  try {
    return (
      localStorage.getItem(launchWhisperStorageKey(conf, agentId, launchQuestion)) ===
      'sent'
    )
  } catch {
    return false
  }
}

export function markLaunchWhisperSent(conf, agentId, launchQuestion) {
  try {
    localStorage.setItem(
      launchWhisperStorageKey(conf, agentId, launchQuestion),
      'sent',
    )
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Legacy v1 `Chat.js` always re-sends the launch whisper when
 * `forceWidgetOpenedWhisper` is set (builder preview passes this flag).
 */
export function shouldBypassLaunchWhisperSentMarker(conf) {
  return conf?.forceWidgetOpenedWhisper === true
}

/**
 * Whether {@link LaunchConversationEffect} should send the launch whisper
 * when the panel is open. Mirrors v1: `forceWidgetOpenedWhisper` bypasses
 * the empty-thread and localStorage "sent" guards.
 *
 * @param {object} input
 * @param {number} input.messageCount
 * @param {Record<string, unknown>} input.conf
 * @param {string|number|null|undefined} input.agentId
 * @param {string|number|null|undefined} input.launchQuestion
 */
export function shouldSendLaunchWhisperOnPanelOpen({
  messageCount,
  conf,
  agentId,
  launchQuestion,
}) {
  if (conf?.sendWidgetOpenedWhisper === false) return false
  if (!agentId) return false
  if (launchQuestion == null || launchQuestion === '') return false
  if (!buildLaunchWhisperPayload(conf)) return false

  const force = shouldBypassLaunchWhisperSentMarker(conf)
  if (messageCount > 0 && !force) return false
  if (
    !force &&
    isLaunchWhisperMarkedSent(conf, agentId, launchQuestion)
  ) {
    return false
  }
  return true
}

/**
 * True when the panel was opened after the in-memory thread was cleared,
 * the launch whisper was already marked sent (so LaunchConversationEffect
 * would skip), and we are not waiting on a prior chat request.
 *
 * @param {object} input
 * @param {boolean} input.open
 * @param {number} input.messageCount
 * @param {boolean} input.threadWasCleared
 * @param {boolean} input.isTyping
 * @param {boolean} input.launchWhisperMarkedSent
 * @param {boolean} [input.sendWidgetOpenedWhisper]
 * @param {boolean} input.hasLaunchPayload
 */
export function shouldResendLaunchWhisperAfterThreadCleared({
  open,
  messageCount,
  threadWasCleared,
  isTyping,
  launchWhisperMarkedSent,
  sendWidgetOpenedWhisper,
  hasLaunchPayload,
}) {
  if (!open) return false
  if (messageCount > 0) return false
  if (!threadWasCleared) return false
  if (isTyping) return false
  if (sendWidgetOpenedWhisper === false) return false
  if (!hasLaunchPayload) return false
  return launchWhisperMarkedSent
}

/**
 * True when the panel is open on an empty thread and a launch whisper is
 * expected but has not produced a bot message yet (first open, recovery, or
 * page-targeting whisper still in flight).
 *
 * @param {object} input
 * @param {boolean} input.open
 * @param {number} input.messageCount
 * @param {Record<string, unknown>} input.conf
 * @param {boolean} input.isTyping
 * @param {boolean} input.threadWasCleared
 * @param {boolean} [input.launchInFlight]
 */
export function shouldShowLaunchLoadingIndicator({
  open,
  messageCount,
  conf,
  isTyping,
  threadWasCleared,
  launchInFlight = false,
}) {
  if (!open || messageCount > 0) return false
  if (conf?.previewMode || conf?.sendWidgetOpenedWhisper === false) return false

  const payload = buildLaunchWhisperPayload(conf)
  if (!payload) return false

  if (isTyping || launchInFlight) return true

  const agentId = conf?.agentId || conf?.chatId
  const launchQuestion = conf?.launchConversationId
  if (!agentId || launchQuestion == null || launchQuestion === '') return false

  const marked = isLaunchWhisperMarkedSent(conf, agentId, launchQuestion)
  if (!marked) return true

  return shouldResendLaunchWhisperAfterThreadCleared({
    open: true,
    messageCount,
    threadWasCleared,
    isTyping: false,
    launchWhisperMarkedSent: marked,
    sendWidgetOpenedWhisper: conf?.sendWidgetOpenedWhisper,
    hasLaunchPayload: true,
  })
}

/**
 * @param {Record<string, unknown>} conf
 */
export function clearConversationCaches(conf) {
  const chatId = conf?.chatId
  if (chatId) {
    try {
      localStorage.removeItem(`messages-${chatId}`)
    } catch {
      // ignore
    }
  }

  const agentId = conf?.agentId || conf?.chatId
  const launchQuestion = conf?.launchConversationId
  if (agentId && launchQuestion != null && launchQuestion !== '') {
    try {
      localStorage.removeItem(
        launchWhisperStorageKey(conf, agentId, launchQuestion),
      )
    } catch {
      // ignore
    }
  }
}
